import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, Handshake, Footprints, Mountain, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './Services.css';

const Services = () => {
  const services = [
    {
      title: "Kennismakingsgesprek",
      description: "Een gratis kennismakingsgesprek van 30 minuten om jouw doelen en verwachtingen te bespreken.",
      duration: "30 minuten",
      price: "Gratis",
      features: [
        "Persoonlijke kennismaking (keuze uit digitaal of wandelend)",
        "Verkenning van jouw hulpvraag",
        "Uitleg over wandelcoaching en het passende traject"
      ],
      icon: <Handshake size={32} />,
      popular: false
    },
    {
      title: "Kickstart traject",
      description: "Een kort en krachtig traject van 3 sessies, voor wie snel resultaat wil behalen. Bijvoorbeeld bij drukte, verminderde energie of behoefte aan meer focus, maar je geen langdurige begeleiding nodig hebt.",
      duration: "3 weken, 2 uur per sessie",
      price: "€600",
      features: [
        "3 wandelcoaching sessies van 2 uur, wekelijks",
        "Praktische tools en inzichten voor direct resultaat",
        "Herkenning van belemmerende patronen om ze te doorbreken"
      ],
      icon: <Footprints size={32} />,
      popular: false
    },
    {
      title: "Duurzaam traject",
      description: "Voor wie echt de tijd wil nemen om diepgaand te werken aan balans, energie en persoonlijke groei. Ideaal bij stress, burn-out of wanneer je merkt dat je vastloopt in je dagelijks leven.",
      duration: "6 weken, 2 uur per sessie",
      price: "€1100",
      features: [
        "5 wandelcoaching sessies van 2 uur, wekelijks. ",
        "1 Reflectiesessie na 4 weken om vooruitgang te evalueren, terugval te voorkomen en veranderingen duurzaam te verankeren",
        "Diepgaand inzicht in jouw patronen, energie en balans",
        "Herkennen en versterken van wat jou energie geeft",
        "Optionele extra sessies mogelijk tegen scherp tarief"
      ],
      icon: <Mountain size={32} />,
      popular: true
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Kennismaking",
      description: "We starten met een gratis intakegesprek van 30 minuten om jouw hulpvraag, doelen en verwachtingen te verkennen."
    },
    {
      step: "2",
      title: "Persoonlijk plan",
      description: "Samen stellen we een traject op maat samen dat aansluit bij jouw situatie en wat jij wilt bereiken."
    },
    {
      step: "3",
      title: "Wandelcoaching Sessies",
      description: "We wandelen samen in de natuur, waarbij je actief aan de slag gaat met jouw doelen. Tijdens de sessies ontdek je inzichten, oefen je met nieuwe gewoonten en vergroot je je energie en zelfbewustzijn."
    },
    {
      step: "4",
      title: "Reflectie & Groei",
      description: "Tijdens het traject evalueren we je voortgang en passen we het plan aan waar nodig. Bij het Duurzaam traject is er bovendien een reflectiesessie na 1 maand om duurzame verandering te verankeren."
    }
  ];

  return (
    <div className="services-page">
      <SEO
        title="Wandelcoaching Diensten en Trajecten - CtheNature | Coaching Pakketten"
        description="Ontdek onze wandelcoaching diensten: Kennismakingsgesprek (gratis), Kickstart traject (3 sessies) en Duurzaam traject (6 weken). Professionele coaching in de natuur voor persoonlijke ontwikkeling, stressmanagement en balans."
        keywords="wandelcoaching diensten, wandelcoaching trajecten, coaching pakketten, wandelcoaching sessies, kennismakingsgesprek coaching, kickstart coaching, duurzaam coaching traject, wandelcoaching prijzen, coaching in de natuur, persoonlijke ontwikkeling traject, stressmanagement coaching, burn-out coaching traject"
        url="/diensten"
      />
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-background">
          <img 
            //src="https://images.unsplash.com/photo-1551632811-561732d1e306?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
            src="/uitzicht.jpg"
            alt="Wandelcoaching diensten en trajecten in de natuur - CtheNature" 
            className="services-hero-background-img"
          />
          <div className="services-hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="services-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="services-hero-title">Wandelend naar balans en persoonlijke groei</h1>
            <p className="services-hero-subtitle">
            Ervaar hoe wandelen in de natuur je helpt om rust, energie en inzicht te krijgen. Of je nu snel resultaat wilt zien of diepgaand wilt werken aan je balans, samen vinden we het traject dat het beste past bij jouw situatie. Zo kom je stap voor stap dichter bij jezelf en je doelen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section services-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Mijn Wandelcoach Trajecten</h2>
            <p className="section-subtitle">
              Kies het traject dat het beste bij jouw behoeften en doelen past. (Alle prijzen zijn inclusief 21% BTW)
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                className={`service-card ${service.popular ? 'popular' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {service.popular && (
                  <div className="popular-badge">Meest Gekozen</div>
                )}
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <div className="service-details">
                  <div className="service-detail">
                    <Clock size={16} />
                    <span>{service.duration}</span>
                  </div>
                  <div className="service-price">{service.price}</div>
                </div>
                <ul className="service-features">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to={service.title === "Intakegesprek" ? "/booking" : `/contact?service=${encodeURIComponent(service.title)}`}
                  className="btn btn-primary service-btn"
                >
                  Boek Nu
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section process-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Hoe het werkt</h2>
            <p className="section-subtitle">
            Een eenvoudig en duidelijk proces om jouw wandelcoaching te starten en resultaten te boeken.
            </p>
          </motion.div>

          <div className="process-grid">
            {processSteps.map((step, index) => (
              <motion.div 
                key={index}
                className="process-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="process-number">{step.step}</div>
                <h3 className="process-title">{step.title}</h3>
                <p className="process-description">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Klaar om te Starten?</h2>
            <p className="cta-subtitle">
              Neem contact op voor een gratis kennismakingsgesprek en ontdek welke 
              dienst het beste bij jou past.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Gratis kennismaking
              </Link>
              <Link to="/over-ons" className="btn btn-secondary">
                Meer over mij
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
